import React, { useState, useRef } from 'react'
import { useTranslation } from '@/hooks/useTranslation';
import { useGame } from '@/app/providers/GameContextProvider';
import { useGlobalPopup } from '@/app/providers/GlobalPopupProvider';
import { AUDIO, useAudio } from '@/app/providers/AudioProvider';

interface SeedModebuttonProps {
    isSelected: boolean;
    onClick: () => void;
}

const SeedModeButton = ({ isSelected, onClick }: SeedModebuttonProps) => {
    const [isPressed, setIsPressed] = useState<boolean>(false);
    const [showCircle, setShowCircle] = useState<boolean>(false);
    const [circleAnimKey, setCircleAnimKey] = useState<number>(0);
    const circleTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const { t } = useTranslation();
    const { room, gameState } = useGame();
    const { showPopup } = useGlobalPopup();
    const { play } = useAudio();
    const isGameMaster = room?.sessionId === gameState?.gameMasterId;

    return (
        <div className="relative w-16 h-16 md:h-6">
            <div
                className={`${!isSelected ? 'opacity-50' : ''} h-16 md:h-6 w-16 shadow-black/20 shadow-md relative rounded-lg text-center py-1 ${isGameMaster ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'} group`}
                onMouseEnter={() => {play(AUDIO.HOVER)}}
                onMouseDown={() => {
                    setIsPressed(true);
                }}
                onMouseUp={() => {
                    setIsPressed(false);
                    if (!isGameMaster) {
                        showPopup({
                            type: 'error',
                            message: t('lobby.onlyGameMasterCan'),
                        });
                        return;
                    }
                    onClick();
                    setShowCircle(true);
                    setCircleAnimKey(circleAnimKey + 1);
                    if (circleTimeoutRef.current) {
                        clearTimeout(circleTimeoutRef.current);
                    }
                    circleTimeoutRef.current = setTimeout(() => {
                        setShowCircle(false);
                    }, 200);
                }}
                onMouseLeave={() => {
                    setIsPressed(false);
                }}
                style={{
                    background: 'radial-gradient(circle, #4A1818 0%, #AC3939 100%)'
                }}
            >
                {/* Glow: pressed shows selected glow, otherwise hover or selected */}
                <div
                    className={
                        `absolute top-[-1px] left-[-1px] w-[67px] h-[28px] rounded-lg pointer-events-none z-40 duration-100 group-hover:bg-[#FFF6CA]/20
									${isPressed
                            ? 'opacity-100 border-[2px] border-[#FFF6CA] shadow-[0px_0px_15px_#FFF6CA,inset_0px_0px_10px_#FFF6CA]'
                            : isSelected
                                ? 'opacity-100 border-[2px] border-[#FFF6CA] shadow-[0px_0px_10px_#FFF6CA,inset_0px_0px_5px_#FFF6CA] group-hover:opacity-100 group-hover:bg-[#FFF6CA]/20 group-hover:shadow-[0px_0px_10px_#FFF6CA,inset_0px_0px_10px_#FFF6CA] group-hover:border-[2px] group-hover:border-[#FFF6CA]'
                                : 'opacity-0 group-hover:opacity-100 group-hover:shadow-[0px_0px_10px_#FFF6CA,inset_0px_0px_10px_#FFF6CA] group-hover:border-[2px] group-hover:border-[#FFF6CA]'
                        }
									`
                    }
                ></div>



                <div className='absolute top-0 left-0 w-full h-full overflow-hidden flex items-center justify-center rounded-lg'>
                    {/* Top highlight */}
                    <div className="absolute top-0 left-0 w-full h-full rounded-lg pointer-events-none z-20 border-t-[2px] border-[#EFE9E0]/70"></div>
                    {/* White top glow */}
                    <div className="top-0 -translate-y-1/2 absolute w-[140%] h-[25px] bg-gradient-to-b from-white to-transparent rounded-[60%]"></div>
                    {/* Growing white circle animation on press */}
                    {showCircle && (
                        <div
                            key={circleAnimKey}
                            className="absolute left-1/2 top-1/2 z-50 pointer-events-none"
                            style={{
                                transform: 'translate(-50%, -50%) scale(0)',
                                animation: 'growCircle 0.2s cubic-bezier(0.4,0,0.2,1) forwards',
                                width: '60px',
                                height: '60px',
                                borderRadius: '9999px',
                                background: 'white',
                                opacity: 0.7,
                                overflow: 'hidden',
                            }}
                        />
                    )}
                </div>


            </div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 translate-y-[22px] md:translate-y-[-8px] w-full text-center uppercase text-white user-select-none select-none z-[100] pointer-events-none">
                <div className="text-center w-full text-shadow shadow-black">{t('play.seedMode.seed')}</div>
                <div className="absolute top-0 left-0 text-center w-full text-shadow-border shadow-yellow-700">{t('play.seedMode.seed')}</div>
            </div>
        </div>

    )
}

export default SeedModeButton